import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";

import { Button } from "../../../components/core/basic/Button";
import { AD_TYPES } from "../../../components/core/blocks/AdPlan/constants";
import FormGroupTile from "../../../components/core/basic/FormGroupTile";
import { useFetch } from "../../../hooks/api";
import { formatPercent } from "../../../utils/formatNumber";
import { Link } from "@tanstack/react-router";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { Checkbox } from "../../../components/fields/base/Checkbox";

const getFormattedDescription = (t, clicks, viewImpressions, conversions, days, isVCPM, lowercase) => {
  let sentence;

  if (conversions && (isVCPM ? viewImpressions : clicks)) {
    if (isVCPM) {
      sentence = t(
        ($) =>
          $.advertising[
            "Keywords and targets will be harvested when there are at least conversions and view impressions in the previous days"
          ],
        {
          conversions,
          viewImpressions,
          days,
        },
      );
    } else {
      sentence = t(
        ($) =>
          $.advertising[
            "Keywords and targets will be harvested when there are at least conversions and clicks in the previous days"
          ],
        {
          conversions,
          clicks,
          days,
        },
      );
    }
  } else if (conversions) {
    sentence = t(
      ($) =>
        $.advertising[
          "Keywords and targets will be harvested when there are at least conversions in the previous days"
        ],
      {
        conversions,
        days,
      },
    );
  } else if (isVCPM && viewImpressions) {
    sentence = t(
      ($) =>
        $.advertising["Keywords and targets will be harvested when there are view impressions in the previous days"],
      {
        viewImpressions,
        days,
      },
    );
  } else if (clicks) {
    sentence = t(
      ($) => $.advertising["Keywords and targets will be harvested when there are clicks in the previous days"],
      {
        clicks,
        days,
      },
    );
  } else {
    sentence = t(($) => $.advertising["Keywords and targets will be harvested in the previous days"], { days });
  }

  if (lowercase) {
    sentence = sentence.toLowerCase();
  }

  let conversionRateSuffix = "";
  if (!isVCPM && clicks && conversions) {
    const conversionRate = conversions / clicks;
    conversionRateSuffix = t(($) => $.advertising["(a conversion rate greater than {rate})"], {
      rate: formatPercent(conversionRate),
    });
  }

  return (
    <>
      <strong>{sentence}</strong>
      {conversionRateSuffix ?
        <> {conversionRateSuffix}</>
      : null}
    </>
  );
};

const HarvestingConfiguration = ({
  saveHarvestingSettings,
  onChange,
  disableSubmitButton = false,
  orgLayout = false,
  state = {},
  textColor = null,
  onSubmit = null,
  readOnly,
}) => {
  const { t } = useTranslation(["advertising", "common"]);
  const [showConversionErrorMessage, setShowConversionErrorMessage] = useState(false);

  const {
    harvestingClicks,
    harvestingConversions,
    harvestingDays,
    harvestingViewImpressions,
    manualHarvestingEnabled,
  } = state ?? {};

  const { data: orgHarvestingSettings } = useFetch(
    ["orgHarvestingSettings"],
    "/org_bid_budget_settings",
    {},
    {
      default: [],
      enabled: !orgLayout,
      select: (d) => d.data,
    },
  );

  const spacing = orgLayout ? 3 : 0;
  const textStyles = {
    color:
      orgLayout ? "inherit"
      : textColor ? textColor
      : "#73706E",
    fontWeight: orgLayout ? "bold" : 560,
  };

  // Description variables
  const descriptionClicks =
    manualHarvestingEnabled || orgLayout ? harvestingClicks : orgHarvestingSettings?.harvesting_clicks;
  const descriptionConversions =
    manualHarvestingEnabled || orgLayout ? harvestingConversions : orgHarvestingSettings?.harvesting_conversions;
  const descriptionViewImpressions =
    manualHarvestingEnabled || orgLayout ? harvestingViewImpressions : (
      orgHarvestingSettings?.harvesting_view_impressions
    );
  const descriptionDays =
    manualHarvestingEnabled || orgLayout ? harvestingDays : orgHarvestingSettings?.harvesting_days;
  const isVCPM = state.sdBidType === "vcpm";

  const formattedSettingsDescription = getFormattedDescription(
    t,
    descriptionClicks,
    descriptionViewImpressions,
    descriptionConversions,
    descriptionDays,
    isVCPM,
    false,
  );

  const formattedVCPMSettingsDescription =
    orgLayout ?
      getFormattedDescription(
        t,
        descriptionClicks,
        descriptionViewImpressions,
        descriptionConversions,
        descriptionDays,
        true,
        true,
      )
    : "";
  return (
    <Row
      className="w-100"
      style={{
        color:
          orgLayout ? "inherit"
          : textColor ? textColor
          : "#73706E",
      }}
    >
      <Col className="p-3">
        <Row className={"pt-3"}>
          <h3 className={`${orgLayout ? "fs-1" : "fs-2"} ps-${orgLayout ? 4 : 2}`} style={textStyles}>
            {t(($) => $.advertising["Harvesting Rules"])}
          </h3>
          <div className={`ms-${spacing} fs-standard`}>
            <p className={`fs-standard ${orgLayout ? "pb-2" : "py-2"}`} style={{ color: textStyles.color }}>
              {t(
                ($) => $.advertising["Customize criteria for keyword and target harvesting using the settings below."],
              )}{" "}
              <span>
                {orgLayout ?
                  t(
                    ($) =>
                      $.advertising[
                        "These settings will be the default for all media plan harvesting and can be overridden on each media plan under the Advanced Targeting menu."
                      ],
                  )
                : t(
                    ($) =>
                      $.advertising[
                        "These settings will override the defaults set in the Placement Configuration menu and will apply only to this media plan."
                      ],
                  )
                }
              </span>
            </p>
            {!orgLayout && !state.manualHarvestingEnabled && (
              <p className={`fs-standard py-2`} style={{ color: textStyles.color }}>
                {t(($) => $.advertising["This media plan is currently using the"])}{" "}
                <strong>{t(($) => $.advertising["organization-level harvesting settings"])}</strong>
                {t(($) => $.advertising[". To modify them visit the"])}{" "}
                <Link
                  to="/user/advertising/settings"
                  style={{
                    color: trellisPalette[13],
                  }}
                  className="text-decoration-none"
                  target="_blank"
                >
                  {t(($) => $.advertising["Advertising Configuration menu"])}
                </Link>
                {t(($) => $.advertising[". To modify harvesting settings on this media plan only, check the"])}{" "}
                <strong>{t(($) => $.advertising["Enable Rules-Based Harvesting"])}</strong>{" "}
                {t(($) => $.advertising["option below to enable harvesting rules fields."])}
              </p>
            )}
            {harvestingConversions ?
              <p className="info-text d-inline-block fs-standard mt-2 p-3" style={{ color: textStyles.color }}>
                {formattedSettingsDescription}.{" "}
                {orgLayout ?
                  <span>
                    {t(($) => $.advertising["For Sponsored Display media plans where the cost type is set to vCPM,"])}{" "}
                    {formattedVCPMSettingsDescription}.
                  </span>
                : null}
              </p>
            : null}
          </div>
        </Row>
        {!orgLayout && (
          <Row className="pt-5">
            <Col xs={12} lg={9}>
              <h5 className={`m-0 ps-${spacing} text-start`} style={textStyles}>
                {t(($) => $.advertising["Override Rules-Based Harvesting"])}
              </h5>
              <div className={`ms-${spacing} d-flex align-items-center fs-standard py-4 py-lg-1`}></div>
            </Col>
            <Col xs={12} lg={3}>
              <Checkbox
                label={t(($) => $.advertising["Override Rules-Based Harvesting"])}
                value={state.manualHarvestingEnabled}
                onChange={() => {
                  onChange({
                    manualHarvestingEnabled: !state.manualHarvestingEnabled,
                  });
                }}
                isDisabled={readOnly}
              />
            </Col>
          </Row>
        )}
        <Row className="pt-5">
          <Col xs={12} lg={9}>
            <h5 className={`m-0 ps-${spacing} text-start`} style={textStyles}>
              {t(($) => $.advertising["Conversions"])}
            </h5>
            <div className={`ms-${spacing} d-flex align-items-center fs-standard py-4 py-lg-1`}>
              {t(($) => $.advertising["The minimum number of conversions to trigger harvesting."])}
            </div>
          </Col>
          <Col xs={12} lg={3}>
            <FormGroupTile
              defaultValue={harvestingConversions}
              type="formcontrol"
              stateName="harvestingConversions"
              formControlType={"number"}
              disabled={(!manualHarvestingEnabled && !orgLayout) || readOnly}
              style={{ textAlign: "center" }}
              handleChildFormElement={(key, value) => {
                if (!parseInt(value)) {
                  setShowConversionErrorMessage(true);
                } else {
                  setShowConversionErrorMessage(false);
                }

                onChange({ [key]: parseInt(value) });
              }}
            />
            {showConversionErrorMessage && (
              <small className="text-danger d-block text-center pt-2">
                {t(($) => $.advertising["At least 1 conversion is required for this field."])}
              </small>
            )}
          </Col>
        </Row>
        {(
          !(
            [AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(state.adType) &&
            state.sdBidType === "vcpm"
          ) || orgLayout
        ) ?
          <Row className="pt-5">
            <Col xs={12} lg={9}>
              <h5 className={`m-0 ps-${spacing} text-start`} style={textStyles}>
                {t(($) => $.advertising["Clicks"])}
              </h5>
              <div className={`ms-${spacing} d-flex align-items-center fs-standard py-4 py-lg-1`}>
                {/* One <span> for the whole run: this div is `d-flex`, so wrapping each fragment
                    separately would turn one flex item into three. */}
                <span>
                  {t(($) => $.advertising["The maximum number of clicks to trigger harvesting."])}{" "}
                  <span>
                    {orgLayout ?
                      t(
                        ($) =>
                          $.advertising[
                            "This threshold will be applied to all media plans except for Sponsored Display plans where the cost type is set to vCPM, in which case the views threshold will be used."
                          ],
                      )
                    : ""}
                  </span>{" "}
                  {t(
                    ($) =>
                      $.advertising[
                        "If this field is set to 0 only conversions will be considered as a criteria for harvesting."
                      ],
                  )}
                </span>
              </div>
            </Col>
            <Col xs={12} lg={3}>
              <FormGroupTile
                defaultValue={harvestingClicks}
                type="formcontrol"
                formControlType={"number"}
                stateName="harvestingClicks"
                style={{ textAlign: "center" }}
                disabled={(!manualHarvestingEnabled && !orgLayout) || readOnly}
                handleChildFormElement={(key, value) => onChange({ [key]: parseInt(value) })}
                key={1}
              />
            </Col>
          </Row>
        : null}
        {(
          ([AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(state.adType) &&
            state.sdBidType === "vcpm") ||
          orgLayout
        ) ?
          <Row className="pt-5">
            <Col xs={12} lg={9}>
              <h5 className={`m-0 ps-${spacing} text-start`} style={textStyles}>
                {t(($) => $.advertising["Views"])}
              </h5>
              <div className={`ms-${spacing} d-flex align-items-center fs-standard py-4 py-lg-1`}>
                {t(
                  ($) =>
                    $.advertising[
                      "The maximum number of viewable impressions for Sponsored Display media plans with their cost type set to vCPM."
                    ],
                )}
              </div>
            </Col>
            <Col xs={12} lg={3}>
              <FormGroupTile
                defaultValue={harvestingViewImpressions}
                type="formcontrol"
                stateName="harvestingViewImpressions"
                formControlType={"number"}
                disabled={(!manualHarvestingEnabled && !orgLayout) || readOnly}
                style={{ textAlign: "center" }}
                handleChildFormElement={(key, value) => onChange({ [key]: parseInt(value) })}
              />
            </Col>
          </Row>
        : null}

        <Row className="pt-5">
          <Col xs={12} lg={9}>
            <h5 className={`m-0 ps-${spacing} text-start`} style={textStyles}>
              {t(($) => $.advertising["Number of Days"])}
            </h5>
            <div className={`ms-${spacing} d-flex align-items-center fs-standard py-4 py-lg-1`}>
              {t(($) => $.advertising["The number of days we look back to evaluate the harvesting conditions."])}
            </div>
          </Col>
          <Col xs={12} lg={3}>
            <FormGroupTile
              defaultValue={harvestingDays}
              type="formcontrol"
              stateName="harvestingDays"
              disabled={(!manualHarvestingEnabled && !orgLayout) || readOnly}
              formControlType={"number"}
              style={{ textAlign: "center" }}
              handleChildFormElement={(key, value) => {
                onChange({ [key]: parseInt(value) });
              }}
            />
          </Col>
        </Row>

        {(orgLayout || typeof onSubmit === "function") && (
          <Row className="pt-5">
            <Col className="d-flex justify-content-center">
              <Button
                isDisabled={disableSubmitButton || showConversionErrorMessage || readOnly}
                onPress={() => (typeof onSubmit === "function" ? onSubmit() : saveHarvestingSettings())}
              >
                {t(($) => $.common.actions["Save"])}
              </Button>
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
};

export default HarvestingConfiguration;
